import { Layout } from '@bridebook/models/source/models/Weddings.types';
import { getBaseUrlFromEnvs } from '@bridebook/toolbox/src/env';
import { pickBy } from 'lodash';
import { FontPair } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/font-config';
import { Palette } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/palette-config';
import { PublicUrls } from '@bridebook/web/lib/url-helper';
import { type MotifOgTheme, serializeMotifOgTheme } from './motif-og-theme';

export type WeddingWebsiteOgImageParams = {
  text?: string;
  palette?: string;
  fontPair?: string;
  photoPath?: string;
  layout?: Layout;
  motif?: MotifOgTheme;
};

type ClassicTheme = Pick<WeddingWebsiteOgImageParams, 'palette' | 'fontPair'>;

/**
 * The classic palette and font pair the image route should render, if any.
 *
 * A resolved motif theme carries its own colours and font, so it needs neither. For a motif website
 * whose theme could not be read, the stored classic values are whatever the couple picked before
 * switching and can look nothing like their site, so the share image falls back to Bridebook.
 */
const classicTheme = ({
  layout,
  motif,
  palette,
  fontPair,
}: WeddingWebsiteOgImageParams): ClassicTheme => {
  if (motif) return {};
  if (layout === Layout.Motif) return { palette: Palette.Bridebook, fontPair: FontPair.Bridebook };
  return { palette, fontPair };
};

export const getWeddingWebsiteOgImageUrlParams = (params: WeddingWebsiteOgImageParams): string => {
  const { text, photoPath, motif } = params;
  const query: Omit<WeddingWebsiteOgImageParams, 'layout' | 'motif'> = {
    text,
    ...classicTheme(params),
    photoPath,
  };
  const filteredParams = pickBy(query, (value) => value != null && value !== '');
  const searchParams = new URLSearchParams(filteredParams);
  if (motif) searchParams.set('motif', serializeMotifOgTheme(motif));
  return searchParams.toString();
};

export const getWeddingWebsiteOgImageUrl = (params: WeddingWebsiteOgImageParams): string => {
  const baseUrl = getBaseUrlFromEnvs();
  const ogImagePath = PublicUrls.weddingWebsite.ogImage(getWeddingWebsiteOgImageUrlParams(params));
  return `${baseUrl}${ogImagePath}`;
};
